/**
 * useCardPlayback.js
 *
 * Per-card playback state machine — PLAYBACK-HANDOFF.md §7.1 lifecycle:
 *   idle -> (▶ pressed) -> requesting -> buffering -> ready -> playing -> done
 *                              \-- cache hit -----------------^ (straight to done)
 *
 * Ported from the mock FE's per-card functions (`startTransfer`,
 * `startPlaybackWithCacheCheck`, `requestPlayback`, `handleAck`,
 * `handleDone`, `markReady`, `scheduleAutoStart`, `handlePlayToggle`,
 * `transferBtnMode`, `teardownCardPlayers`, `resetTransferState`,
 * `armUploadWatchdog`) with the same field names where practical, so the
 * handoff's citations of that file stay legible against this port.
 *
 * Key differences from the mock, all per explicit owner/user decisions:
 *  - No Stop button/state (2026-09-12: "Bỏ Stop, giữ 3 trạng thái còn lại").
 *    transferBtnMode is 'start' | 'sending' | 'done' only. The row's 'sending'
 *    label ("Hủy", 2026-09-12 feedback after a request that never got an ack
 *    looked like a frozen page) is NOT a re-introduction of Stop: it is a
 *    purely local UI reset (handleCancelSending) with no wire message of its
 *    own — the device is never told to stop, only this card stops waiting.
 *  - Other cards are never greyed/blocked (handoff §4.4 point 6, superseding
 *    the original §7.3) — this hook only ever produces a tooltip hint via
 *    playbackDeviceContext, never a `disabled`.
 *  - Download-enable is a custom rule (2026-09-12): enabled once buffered
 *    seconds reach 60s, OR once the clip's real full duration has landed
 *    while still under 60s (a genuinely short complete recording). A clip
 *    stuck below 60s because a transfer was cancelled/preempted stays
 *    disabled ("đó là mẩu cụt").
 *
 * State is kept in a mutable ref (not useState/useReducer) and rendered via
 * a manual re-render trigger. Deliberate: this state machine is driven by a
 * dozen independent async sources (WebSocket messages, hls.js events, timers,
 * fetch chains) that all mutate the SAME record — modelling it as one
 * useReducer action per mutation would multiply the surface for stale-closure
 * bugs across this many call sites for no benefit, since every mutator here
 * already fully owns `cs` (there is exactly one instance per card).
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  subscribePlayback,
  requestPlayback as sendPlaybackRequest,
  cardKey as makeCardKey,
  camNumFromSegmentUid,
} from './playbackTransport';
import {
  findCachedRecording,
  playlistUrlFor,
  downloadPartialSegment,
  downloadFullSegment,
} from './storageClient';
import { mapErrorMessage } from './errorMap';
import { fmtClipMmss } from './format';
import { resolveDecodeTier } from './decode/tierSelect';
import { createHlsTier } from './decode/hlsTier';
import { createWebCodecsBackend } from './decode/webcodecsBackend';
import { createWasmBackend } from './decode/wasmBackend';
import { startJsTierPump } from './decode/jsTierPump';
import { usePlaybackDeviceContext } from './playbackDeviceContext';

const AUTOSTART_BUFFER_SEC = 12; // owner ruling 2026-09-11, "option A" (handoff §7.2)
const AUTOSTART_MAX_WAIT_MS = 25000; // escape hatch — never leave the viewer staring at a dead card
const PREEMPT_RETRY_MAX = 8;
const PREEMPT_RETRY_FALLBACK_MS = 1500;
const UPLOAD_WATCHDOG_MS = 600000; // 10 min — E-S2-44: a dropped ws/device must not lock every other card forever
const ACK_TIMEOUT_MS = 15000; // no playback_ack at all (PB-2) must surface as a note, not a silent freeze
const DATA_STALL_MS = 20000; // ack said "ok" but storage/CDN never actually serves a byte
const DOWNLOAD_MIN_SEC = 60;
const FULLY_BUFFERED_TOLERANCE_SEC = 2; // duration_ms vs #EXTINF sum can disagree by ~1s (measured)
const PLAYHEAD_POLL_MS = 250;

function currentToken() {
  try { return localStorage.getItem('access-token') || ''; } catch (_) { return ''; }
}

function useForceUpdate() {
  const [, dispatch] = useReducer((x) => x + 1, 0);
  return useCallback(() => dispatch(), []);
}

/**
 * @param {object} args
 * @param {string} args.deviceSerial
 * @param {number} args.cameraNum      1-based (wire convention, §4.2)
 * @param {number} args.camIndex0      0-based (storage-listing convention, §4.5/trap#2)
 * @param {number} args.slotId
 * @param {number} args.tsUs
 * @param {number} args.durationMs     recording's real duration, from the search result
 */
export function useCardPlayback({ deviceSerial, cameraNum, camIndex0, slotId, tsUs, durationMs }) {
  const deviceCtx = usePlaybackDeviceContext();
  const forceUpdate = useForceUpdate();
  const key = makeCardKey(cameraNum, slotId);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wasmContainerRef = useRef(null);

  const csRef = useRef(null);
  if (!csRef.current) {
    csRef.current = {
      key, deviceSerial, cameraNum, camIndex0, slotId, tsUs,
      status: 'idle', // idle|requesting|buffering|ready|playing|done|error
      error: null, note: null,
      deviceUploading: false,
      transferComplete: false,
      everPlayable: false, // sticky — once true, ▶/⭳ stay enabled forever (F11)
      isPlaying: false,
      overlayDismissed: false,
      fromCache: false,
      bufferedSec: 0,
      durationMs: durationMs || 0,
      videoUrl: null,
      decodePath: null,
      segmentUid: null,
      clickAt: null,
      preemptAttempts: 0,
      manualStart: false,
      mseFallbackAttempted: false,
      // VLC-style playhead readout (current position / total duration), shown
      // above the timeline. Separate concept from bufferedSec (§7.4 download
      // progress) -- MSE reads videoRef.currentTime directly; the JS tiers
      // (WebCodecs/WASM) have no native currentTime, so a small pause-aware
      // stopwatch (playClockAccumSec/playClockResumedAtMs) stands in for one.
      currentTimeSec: 0,
      playClockAccumSec: 0,
      playClockResumedAtMs: null,
      // player handles (exactly one live at a time — teardownCardPlayers owns all of them)
      hlsHandle: null,
      jsBackend: null,
      jsPump: null,
      // Runs ALONGSIDE hlsHandle only, in `trackOnly` mode -- see
      // jsTierPump.js's `trackOnly` doc: hls.js's own reload timer is not a
      // reliable source for bufferedSec/completion against this server's
      // playlists, measured 2026-09-12.
      durationWatcher: null,
      // timers
      autoStartTimer: null,
      preemptRetryTimer: null,
      uploadWatchdogTimer: null,
      ackTimeoutTimer: null,
      dataStallTimer: null,
      playheadPollTimer: null,
    };
  }
  const cs = csRef.current;

  const rerender = useCallback(() => forceUpdate(), [forceUpdate]);

  // -----------------------------------------------------------------------
  // Teardown / reset (mock FE `teardownCardPlayers` / `resetTransferState`)
  const cancelAutoStart = useCallback(() => {
    if (cs.autoStartTimer) { clearInterval(cs.autoStartTimer); cs.autoStartTimer = null; }
  }, [cs]);

  const clearUploadWatchdog = useCallback(() => {
    if (cs.uploadWatchdogTimer) { clearTimeout(cs.uploadWatchdogTimer); cs.uploadWatchdogTimer = null; }
  }, [cs]);

  const clearAckTimeout = useCallback(() => {
    if (cs.ackTimeoutTimer) { clearTimeout(cs.ackTimeoutTimer); cs.ackTimeoutTimer = null; }
  }, [cs]);

  // A playback_request that never gets ANY playback_ack back (PB-2: unverified
  // whether the viewer WSS path relays these messages at all) must not look
  // like a frozen page. This only ever writes `note` -- it never changes
  // `status`, so a late ack still lands normally; it exists purely so the
  // spinner's caption stops saying a generic "Đang kết nối…" forever and the
  // Hủy button (below) has something to escape.
  const armAckTimeout = useCallback(() => {
    clearAckTimeout();
    cs.ackTimeoutTimer = setTimeout(() => {
      cs.ackTimeoutTimer = null;
      if (cs.status !== 'requesting') return; // an ack already arrived
      cs.note = 'Chưa nhận được phản hồi từ thiết bị sau 15s. Có thể do đường truyền chưa nối được tới thiết bị — bấm "Hủy" rồi thử lại.';
      rerender();
    }, ACK_TIMEOUT_MS);
  }, [cs, clearAckTimeout, rerender]);

  const clearDataStallTimeout = useCallback(() => {
    if (cs.dataStallTimer) { clearTimeout(cs.dataStallTimer); cs.dataStallTimer = null; }
  }, [cs]);

  // The OTHER half of PB-2's failure modes: the device ACKed (accepted the
  // request), but storage/CDN never actually serves a single byte of the
  // segment (playlist stays empty, or storage never even sees an upload).
  // The ack-timeout above only covers "no ack at all"; once status flips to
  // 'buffering' that timer's guard (status !== 'requesting') makes it a
  // no-op, so without this a stall AFTER a successful ack looked identical
  // to one before it -- both were just a spinner forever. Cleared the moment
  // any real byte count arrives (updateBufferedSec) or a frame ever renders
  // (markReady), whichever first.
  const armDataStallTimeout = useCallback(() => {
    clearDataStallTimeout();
    cs.dataStallTimer = setTimeout(() => {
      cs.dataStallTimer = null;
      if (cs.everPlayable || cs.bufferedSec > 0 || cs.status !== 'buffering') return;
      cs.note = `Thiết bị đã nhận yêu cầu nhưng chưa thấy dữ liệu nào từ storage sau ${Math.round(DATA_STALL_MS / 1000)}s ` +
        '(có thể do đường tải lên 4G yếu, hoặc storage chưa ghép được đoạn này). Bấm "Hủy" để dừng chờ.';
      rerender();
    }, DATA_STALL_MS);
  }, [cs, clearDataStallTimeout, rerender]);

  const releaseUploadingFlag = useCallback(() => {
    cs.deviceUploading = false;
    clearUploadWatchdog();
    deviceCtx.releaseUploading(key);
  }, [cs, clearUploadWatchdog, deviceCtx, key]);

  const clearPlayheadPoll = useCallback(() => {
    if (cs.playheadPollTimer) { clearInterval(cs.playheadPollTimer); cs.playheadPollTimer = null; }
  }, [cs]);

  const teardownPlayers = useCallback(() => {
    cancelAutoStart();
    clearAckTimeout();
    clearDataStallTimeout();
    clearPlayheadPoll();
    if (cs.preemptRetryTimer) { clearTimeout(cs.preemptRetryTimer); cs.preemptRetryTimer = null; }
    if (cs.jsPump) { cs.jsPump.stop(); cs.jsPump = null; }
    if (cs.durationWatcher) { cs.durationWatcher.stop(); cs.durationWatcher = null; }
    if (cs.jsBackend) { try { cs.jsBackend.destroy(); } catch (_) { /* ignore */ } cs.jsBackend = null; }
    if (cs.hlsHandle) { try { cs.hlsHandle.destroy(); } catch (_) { /* ignore */ } cs.hlsHandle = null; }
    if (canvasRef.current) {
      try { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); } catch (_) { /* ignore */ }
    }
    if (wasmContainerRef.current) wasmContainerRef.current.innerHTML = '';
  }, [cs, cancelAutoStart, clearAckTimeout, clearDataStallTimeout, clearPlayheadPoll]);

  // F4: everything describing THIS view that must not survive into the
  // next one. bufferedSec is the dangerous one — deliberately monotonic, so
  // it must be zeroed per new view or the 12s cushion / clip timeline of a
  // second view would see the first view's totals (mock-fe bug, fixed here
  // from the start). currentTimeSec/playClock* are the same trap for the
  // VLC-style playhead readout.
  const resetTransferState = useCallback(() => {
    cs.bufferedSec = 0;
    cs.mseFallbackAttempted = false;
    cs.currentTimeSec = 0;
    cs.playClockAccumSec = 0;
    cs.playClockResumedAtMs = null;
  }, [cs]);

  const armUploadWatchdog = useCallback(() => {
    clearUploadWatchdog();
    cs.uploadWatchdogTimer = setTimeout(() => {
      cs.uploadWatchdogTimer = null;
      if (!cs.deviceUploading) return;
      cs.deviceUploading = false;
      deviceCtx.releaseUploading(key);
      cs.note = 'Không nhận được phản hồi sau 10 phút — bỏ khoá các thẻ khác. ' +
        'Thiết bị có thể vẫn đang tải; nếu bấm thẻ khác mà báo bận thì đợi thêm.';
      rerender();
    }, UPLOAD_WATCHDOG_MS);
  }, [cs, clearUploadWatchdog, deviceCtx, key, rerender]);

  // -----------------------------------------------------------------------
  // Decode-tier attach (handoff §8) — resolved once per page load, shared
  // by every card via decode/tierSelect.js's memoized promise.
  const beginPlaylistPlayback = useCallback((url) => {
    cs.videoUrl = url;
    armPlayheadPoll();
    resolveDecodeTier().then(({ tier, webcodecsCodec }) => {
      if (cs.videoUrl !== url) return; // superseded by a newer view before the probe resolved
      cs.decodePath = tier;
      console.info(`[playback:${key}] decode tier resolved: ${tier}`, url);
      rerender();

      if (tier === 'mse') {
        // Measured 2026-09-12: a single underlying SourceBuffer/codec setup
        // failure makes hls.js dispatch SEVERAL fatal ERROR events back to
        // back in the same tick (bufferAddCodecError, then
        // bufferIncompatibleCodecsError, then bufferAppendError -- all
        // within ~5ms of each other). Without this guard, the first one
        // correctly falls through to the JS tier, then the second/third
        // (one of which isn't even in fallbackEligible, and by then
        // mseFallbackAttempted is already true) each re-run this callback
        // and stomp the just-recovered state back to 'error' -- a real clip
        // showed a permanent "⚠ bufferAppendError" on top of an otherwise
        // fully-buffered card. Only the FIRST fatal/ended-short signal for
        // this attach may ever act; everything after is a stale echo of the
        // same failure and must be ignored outright.
        let hlsDone = false;
        const fallToJsTier = () => {
          if (hlsDone || cs.everPlayable || cs.mseFallbackAttempted) return false;
          hlsDone = true;
          cs.mseFallbackAttempted = true;
          if (cs.hlsHandle) { try { cs.hlsHandle.destroy(); } catch (_) { /* ignore */ } cs.hlsHandle = null; }
          beginJsPlayback(url, webcodecsCodec);
          return true;
        };
        cs.hlsHandle = createHlsTier({
          videoEl: videoRef.current,
          playlistUrl: url,
          getToken: currentToken,
          expectedDurationSec: (cs.durationMs || 0) / 1000,
          callbacks: {
            // Every callback below fires from an async source (hls.js's own
            // event loop) that outlives a single view: guard on cs.videoUrl
            // still matching THIS attach's url, the same pattern already
            // used just above for the tier-resolution race, so a callback
            // that lands after teardownPlayers() (a new click, a cancel)
            // can never mutate a later view's state.
            onBufferedSeconds: (sec) => { if (cs.videoUrl === url) updateBufferedSec(sec); },
            onFragBuffered: () => { if (cs.videoUrl === url) markReady(); },
            onPlaying: () => { if (cs.videoUrl === url) recordFirstFrame(); },
            // The playlist is sealed with #EXT-X-ENDLIST well short of the
            // segment's known full length, and hls.js will never reload it
            // again on its own (see hlsTier.js's onEndedShort doc) -- fall
            // through to the JS-tier pump, which already tolerates a
            // storage `partial` seal that later reopens and keeps growing.
            onEndedShort: () => { if (cs.videoUrl === url) fallToJsTier(); },
            onFatalError: (detail) => {
              if (cs.videoUrl !== url || hlsDone) return; // stale view, or echo of an already-handled failure -- see hlsDone doc above
              // §8: an hls.js fatal error before any frame has ever played
              // must fall through to tier 2/3 rather than surface as a
              // playback error. Originally just the INIT SEGMENT codec
              // reject (historic cause: firmware used to carry an alaw
              // audio track Chrome's MSE parser rejected -- fixed
              // firmware-side, but the fallback stays); widened 2026-09-12
              // to cover a manifest load failure too (storage 404s the
              // playlist until the device's first chunk lands -- hls.js's
              // own retries are bounded and then give up for good, where
              // the JS-tier pump already retries tolerantly forever).
              const fallbackEligible = detail === 'bufferAddCodecError' || detail === 'bufferAppendError' ||
                detail === 'manifestLoadError' || detail === 'manifestLoadTimeOut';
              if (fallbackEligible && fallToJsTier()) return;
              hlsDone = true; // this WAS the terminal outcome -- a later cascade echo must not overwrite it either
              cs.status = 'error';
              cs.error = `Lỗi giải mã: ${detail}`;
              rerender();
            },
          },
        });
        // Runs independently of hls.js's own reload timer -- see
        // jsTierPump.js's `trackOnly` doc for why that timer isn't trusted
        // as the bufferedSec/completion source. `backend`/demux callbacks
        // are irrelevant here; only onBufferedSeconds/onEndlist matter.
        cs.durationWatcher = startJsTierPump({
          playlistUrl: url,
          getToken: currentToken,
          trackOnly: true,
          expectedDurationSec: (cs.durationMs || 0) / 1000,
          callbacks: {
            onBufferedSeconds: (sec) => { if (cs.videoUrl === url) updateBufferedSec(sec); },
            onEndlist: () => { /* playback_done governs transferComplete/status, not this */ },
          },
        });
      } else {
        beginJsPlayback(url, webcodecsCodec);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs, rerender]);

  const beginJsPlayback = useCallback((url, webcodecsCodec) => {
    // Fell through from mse: pick WebCodecs only if tierSelect's own probe
    // actually found a usable codec. Measured 2026-09-12: unconditionally
    // assuming 'webcodecs' here (regardless of webcodecsCodec) sent a real
    // fallback into a WebCodecs backend the browser cannot configure --
    // "WebCodecs: Unsupported configuration. Check isConfigSupported()
    // prior to calling configure()." -- permanently, since WASM (tier 3,
    // "the guaranteed-to-work fallback") was never tried.
    if (cs.decodePath === 'mse') cs.decodePath = webcodecsCodec ? 'webcodecs' : 'wasm';
    const isWebcodecs = cs.decodePath === 'webcodecs';
    // As with the mse tier above, every callback here fires from an async
    // source (the chunk pump's fetch loop, the decoder's own async error
    // path) that outlives a single view -- guard on cs.videoUrl still
    // matching THIS attach's url so a stale callback from a torn-down view
    // can never mutate a later one's state.
    const backend = isWebcodecs
      ? createWebCodecsBackend({
        canvasEl: canvasRef.current,
        codecString: webcodecsCodec,
        callbacks: {
          onFirstFrame: () => { if (cs.videoUrl === url) { markReady(); recordFirstFrame(); } },
          onStall: () => { /* not surfaced in production UI, per handoff §10 */ },
          onError: (message) => { if (cs.videoUrl !== url) return; cs.status = 'error'; cs.error = `WebCodecs: ${message}`; rerender(); },
        },
      })
      : createWasmBackend({
        containerEl: wasmContainerRef.current,
        callbacks: {
          onFirstFrame: () => { if (cs.videoUrl === url) { markReady(); recordFirstFrame(); } },
          onStall: () => { /* not surfaced in production UI */ },
          onError: (message) => { if (cs.videoUrl !== url) return; cs.status = 'error'; cs.error = `WASM: ${message}`; rerender(); },
        },
      });
    cs.jsBackend = backend;
    cs.jsPump = startJsTierPump({
      playlistUrl: url,
      getToken: currentToken,
      backend,
      expectedDurationSec: (cs.durationMs || 0) / 1000,
      callbacks: {
        onBufferedSeconds: (sec) => { if (cs.videoUrl === url) updateBufferedSec(sec); },
        onFirstAuFed: () => { /* markReady already called via onFirstFrame */ },
        onEndlist: () => { /* playback_done governs transferComplete/status, not this */ },
        onError: (e) => { if (cs.videoUrl !== url) return; cs.status = 'error'; cs.error = (e && e.message) || String(e); rerender(); },
      },
    });
    rerender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs, rerender]);

  // -----------------------------------------------------------------------
  // Clip timeline source (§7.4) — monotonic, never from bytes.
  const updateBufferedSec = useCallback((totalDurationSec) => {
    if (totalDurationSec == null) return;
    const next = Math.max(cs.bufferedSec || 0, totalDurationSec);
    if (next === cs.bufferedSec) return;
    cs.bufferedSec = next;
    if (next > 0) clearDataStallTimeout(); // real bytes landed -- the ack->data stall is over
    // Refresh the shared uploading-card snapshot so OTHER cards' preemption
    // ETA hint (playbackDeviceContext.estimateRemainingSeconds) improves as
    // real data arrives, instead of forever reading the 0 it was claimed
    // with (push-based, no 1Hz poll needed).
    if (cs.deviceUploading) {
      deviceCtx.claimUploading({ cardKey: key, cameraNum, slotId, clickAt: cs.clickAt, durationMs: cs.durationMs, bufferedSec: next });
    }
    rerender();
  }, [cs, rerender, deviceCtx, key, cameraNum, slotId, clearDataStallTimeout]);

  // -----------------------------------------------------------------------
  // VLC-style playhead readout (current position / total duration), shown
  // above the timeline in place of the old static duration/camera labels.
  // MSE has a real currentTime; the JS tiers use the pause-aware stopwatch
  // below since neither backend exposes a native playhead.
  const startPlayClock = useCallback(() => {
    if (cs.playClockResumedAtMs == null) cs.playClockResumedAtMs = performance.now();
  }, [cs]);

  const stopPlayClock = useCallback(() => {
    if (cs.playClockResumedAtMs != null) {
      cs.playClockAccumSec += (performance.now() - cs.playClockResumedAtMs) / 1000;
      cs.playClockResumedAtMs = null;
    }
  }, [cs]);

  const armPlayheadPoll = useCallback(() => {
    clearPlayheadPoll();
    cs.playheadPollTimer = setInterval(() => {
      let sec;
      if (cs.decodePath === 'mse') {
        const v = videoRef.current;
        sec = v ? v.currentTime || 0 : 0;
      } else if (cs.playClockResumedAtMs != null) {
        sec = cs.playClockAccumSec + (performance.now() - cs.playClockResumedAtMs) / 1000;
      } else {
        sec = cs.playClockAccumSec;
      }
      const durationSec = (cs.durationMs || 0) / 1000;
      if (durationSec > 0) sec = Math.min(sec, durationSec);
      if (Math.abs(sec - (cs.currentTimeSec || 0)) >= 0.2) {
        cs.currentTimeSec = sec;
        rerender();
      }
    }, PLAYHEAD_POLL_MS);
  }, [cs, clearPlayheadPoll, rerender]);

  // -----------------------------------------------------------------------
  // markReady (§7.1): the single choke point that unlocks ▶/⭳ and starts
  // the autostart cushion. F11: everPlayable is STICKY (survives a later
  // failed re-view); readyMs-equivalent latency is not surfaced in
  // production UI so is not tracked here.
  // Cosmetic only (mock FE `recordFirstFrame`) — status='playing' is never
  // branched on by any behaviour in this feature, it only makes the status
  // pill/log line say "playing" instead of "ready" once a frame has
  // actually rendered, matching the §7.1 lifecycle diagram.
  const recordFirstFrame = useCallback(() => {
    if (cs.status !== 'error' && cs.status !== 'done') cs.status = 'playing';
    rerender();
  }, [cs, rerender]);

  const markReady = useCallback(() => {
    clearDataStallTimeout(); // a frame rendered -- definitely not stalled
    if (cs.everPlayable && cs.status !== 'requesting' && cs.status !== 'buffering') return; // already ready
    if (cs.status === 'requesting' || cs.status === 'buffering') cs.status = 'ready';
    cs.everPlayable = true;
    rerender();
    scheduleAutoStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs, rerender, clearDataStallTimeout]);

  // -----------------------------------------------------------------------
  // Autostart cushion (§7.2, owner ruling 2026-09-11 "option A"). The ONLY
  // code path allowed to call play()/resume() without a user click — every
  // decode-tier module in this feature is written to never call it itself
  // (trap #6: "If you have more than one place that can call play(), you
  // have this bug").
  const playableSeconds = useCallback(() => {
    if (cs.decodePath !== 'mse') return cs.bufferedSec || 0;
    const v = videoRef.current;
    const b = v && v.buffered;
    if (!b || !b.length) return 0;
    const from = v.currentTime || 0;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= from + 0.1 && b.end(i) > from) return b.end(i) - from;
    }
    return 0;
  }, [cs]);

  const autoStartPlayback = useCallback(() => {
    cancelAutoStart();
    cs.manualStart = false;
    if (cs.jsBackend) {
      if (cs.jsBackend.paused) { cs.jsBackend.resume(); cs.isPlaying = true; startPlayClock(); }
      rerender();
      return;
    }
    const v = videoRef.current;
    if (v && v.paused) {
      v.play().then(() => { cs.isPlaying = true; rerender(); }).catch(() => { /* autoplay rejection is not fatal */ });
    }
  }, [cs, cancelAutoStart, startPlayClock, rerender]);

  const scheduleAutoStart = useCallback(() => {
    cancelAutoStart();
    const waitFrom = performance.now();
    const tick = () => {
      const have = playableSeconds();
      const durationSec = (cs.durationMs || 0) / 1000;
      const waited = performance.now() - waitFrom;
      if (have >= AUTOSTART_BUFFER_SEC) { cancelAutoStart(); autoStartPlayback(); return; }
      // A short or fully-cached clip can hold less than the cushion and still be complete.
      if (durationSec > 0 && have >= durationSec - FULLY_BUFFERED_TOLERANCE_SEC) { cancelAutoStart(); autoStartPlayback(); return; }
      if (waited >= AUTOSTART_MAX_WAIT_MS) {
        cs.note = 'Phát sớm, CÓ THỂ GIẬT — chờ đệm quá lâu';
        rerender();
        cancelAutoStart();
        autoStartPlayback();
      }
    };
    cs.autoStartTimer = setInterval(tick, 250);
    tick();
  }, [cs, cancelAutoStart, playableSeconds, autoStartPlayback, rerender]);

  // -----------------------------------------------------------------------
  // Cache-first (§7.6, REQUIRED — asking the device for a segment storage
  // already has can overwrite the composed object under a running player).
  const beginFromCache = useCallback((match, clickAt) => {
    teardownPlayers();
    cs.status = 'done';
    cs.transferComplete = true;
    cs.fromCache = true;
    releaseUploadingFlag();
    cs.error = null;
    cs.note = null;
    cs.clickAt = clickAt;
    resetTransferState();
    cs.segmentUid = match.segment_uid;
    if (match.duration_ms != null) cs.durationMs = match.duration_ms;
    rerender();
    markReady(); // status already 'done' -- this only sets everPlayable + schedules autostart
    beginPlaylistPlayback(match.playlist_url);
  }, [cs, teardownPlayers, releaseUploadingFlag, resetTransferState, rerender, markReady, beginPlaylistPlayback]);

  const requestPlaybackToDevice = useCallback((clickAtOverride) => {
    teardownPlayers();
    cs.status = 'requesting';
    cs.deviceUploading = true;
    cs.transferComplete = false;
    cs.manualStart = false;
    cs.error = null;
    cs.note = null;
    cs.clickAt = clickAtOverride != null ? clickAtOverride : performance.now();
    resetTransferState();
    armUploadWatchdog();
    deviceCtx.claimUploading({ cardKey: key, cameraNum, slotId, clickAt: cs.clickAt, durationMs: cs.durationMs, bufferedSec: cs.bufferedSec });
    deviceCtx.setLastRequestedCardKey(key);
    rerender();
    armAckTimeout();
    console.info(`[playback:${key}] playback_request ->`, { deviceSerial, camera: cameraNum, slotId, tsUs });
    sendPlaybackRequest({ deviceSerial, camera: cameraNum, slotId, tsUs }).catch((err) => {
      clearAckTimeout();
      cs.status = 'error';
      cs.error = err && err.message ? err.message : String(err);
      releaseUploadingFlag();
      rerender();
    });
  }, [cs, teardownPlayers, resetTransferState, armUploadWatchdog, armAckTimeout, clearAckTimeout, deviceCtx, key, cameraNum, slotId, deviceSerial, tsUs, releaseUploadingFlag, rerender]);

  const startPlaybackWithCacheCheck = useCallback(() => {
    cancelAutoStart(); // a fresh view supersedes any pending auto-start
    if (cs.preemptRetryTimer) { clearTimeout(cs.preemptRetryTimer); cs.preemptRetryTimer = null; }
    cs.preemptAttempts = 0; // a human click starts the retry budget over
    const clickAt = performance.now();
    cs.status = 'requesting';
    cs.note = null;
    cs.error = null;
    cs.fromCache = false;
    cs.clickAt = clickAt;
    rerender();
    findCachedRecording({ deviceSerial, camIndex0, tsUs }).then((match) => {
      if (match) beginFromCache(match, clickAt);
      else requestPlaybackToDevice(clickAt);
    });
  }, [cs, cancelAutoStart, deviceSerial, camIndex0, tsUs, rerender, beginFromCache, requestPlaybackToDevice]);

  /** Shared by the tile overlay ▶ and the row's Start/Xem-lại button. */
  const startTransfer = useCallback(() => {
    // F6: re-entry guard -- the overlay is one-shot, but the row button is
    // not, and between the click and deviceUploading=true the card is only
    // 'requesting' (the storage cache probe, up to 2s).
    if (cs.status === 'requesting') return;
    cs.overlayDismissed = true; // one-shot: bare tile from here on, whatever happens next
    startPlaybackWithCacheCheck();
  }, [cs, startPlaybackWithCacheCheck]);

  /**
   * Row button while `transferBtnMode === 'sending'` ("Hủy"). LOCAL ONLY —
   * the wire has no client-initiated cancel message (§4.2: playback_request/
   * playback_ack/playback_done only), so this cannot and does not tell the
   * device to stop; the device may keep uploading this segment regardless.
   * It just stops THIS card waiting (clears the ack/preempt-retry timers,
   * releases the local "uploading" claim so other cards' tooltips recover,
   * and returns the row to 'start') so a request that never gets a
   * playback_ack (PB-2) has a way out besides reloading the page. A real
   * playback_done:cancelled from the device is still handled solely by
   * handleDone above and is untouched by this.
   */
  const handleCancelSending = useCallback(() => {
    // teardownPlayers covers BOTH stall stages this button can appear during:
    // stage 1 (no ack yet -- nothing to tear down, harmless no-op) and stage 2
    // (ack ok already landed, a jsPump/hlsHandle may already be polling
    // storage -- must actually be killed, not just have its timers cleared).
    teardownPlayers();
    if (cs.preemptRetryTimer) { clearTimeout(cs.preemptRetryTimer); cs.preemptRetryTimer = null; }
    releaseUploadingFlag();
    cs.status = 'idle';
    cs.videoUrl = null;
    cs.decodePath = null;
    cs.note = 'Đã hủy chờ. Thiết bị có thể vẫn đang xử lý yêu cầu cũ — nếu bấm lại ngay mà báo bận, hãy đợi thêm.';
    rerender();
  }, [cs, teardownPlayers, releaseUploadingFlag, rerender]);

  const handleVideoAreaClick = useCallback(() => {
    if (cs.overlayDismissed) return;
    startTransfer();
  }, [cs, startTransfer]);

  /** ▶/⏸ small button (§7.1): pause/resume a LOADED player; only once
   * nothing is loaded (or the view ran to the end) does it mean "watch
   * again", which goes through the cache-first path like any other start. */
  const handlePlayToggle = useCallback(() => {
    cancelAutoStart(); // an explicit ▶ overrides the cushion (§7.2)
    if (!cs.everPlayable && (cs.status === 'idle' || cs.status === 'error')) {
      startTransfer();
      return;
    }
    if (cs.jsBackend) {
      if (cs.jsBackend.paused) { cs.jsBackend.resume(); cs.isPlaying = true; startPlayClock(); } else { cs.jsBackend.pause(); cs.isPlaying = false; stopPlayClock(); }
      rerender();
      return;
    }
    const v = videoRef.current;
    if (cs.videoUrl && v) {
      if (v.paused || v.ended) {
        v.play().then(() => { cs.isPlaying = true; rerender(); }).catch(() => {});
      } else {
        v.pause();
        cs.isPlaying = false;
        rerender();
      }
      return;
    }
    startTransfer();
  }, [cs, cancelAutoStart, rerender, startTransfer, startPlayClock, stopPlayClock]);

  // -----------------------------------------------------------------------
  // Wire message handling (handoff §4.2/§4.4). Every card subscribes and
  // filters for itself (playbackTransport broadcasts unfiltered); primary
  // correlation is segment_uid (which carries a 0-based camera index --
  // camNumFromSegmentUid converts it back to 1-based to match cameraNum),
  // falling back to "the card whose click is still outstanding" for a
  // busy/not-found error that carries no uid at all (mock FE
  // `routeReplyToCard()`).
  const isMine = useCallback((msg) => {
    const camNum = camNumFromSegmentUid(msg.segment_uid);
    if (camNum != null && msg.slot_id != null) {
      return makeCardKey(camNum, msg.slot_id) === key;
    }
    return deviceCtx.getLastRequestedCardKey() === key;
  }, [key, deviceCtx]);

  const handleAck = useCallback((msg) => {
    if (msg.status === 'ok') {
      cs.status = 'buffering';
      cs.segmentUid = msg.segment_uid;
      if (msg.duration_ms != null) {
        cs.durationMs = msg.duration_ms;
        if (cs.deviceUploading) {
          deviceCtx.claimUploading({ cardKey: key, cameraNum, slotId, clickAt: cs.clickAt, durationMs: cs.durationMs, bufferedSec: cs.bufferedSec });
        }
      }
      // F5: an idempotent/duplicate ack (same segment re-requested -- §4.4
      // point 2, "a request for the SAME segment already in flight is a
      // resume") must not attach a second player on top of a live one.
      // teardownPlayers() clears any timer from a PRIOR view, including a
      // data-stall one -- so the fresh one for THIS view must be armed after.
      teardownPlayers();
      armDataStallTimeout();
      rerender();
      beginPlaylistPlayback(playlistUrlFor(msg.segment_uid));
      return;
    }
    if (cs.status !== 'requesting') {
      // This card has a LIVE transfer (buffering/playing) and an
      // error/busy ack just arrived for it -- it cannot be about the
      // running upload, so applying it would clear deviceUploading under a
      // transfer that is still going. Only a card still waiting for its
      // own ack may be moved by one.
      return;
    }
    if (msg.message === 'preempting') {
      // Not an error: the device ACCEPTED this request and is stopping the
      // running transfer to serve it. Nothing of ours is uploading in the
      // meantime.
      clearAckTimeout(); // an ack DID arrive -- requestPlaybackToDevice's retry re-arms its own
      releaseUploadingFlag();
      cs.preemptAttempts = (cs.preemptAttempts || 0) + 1;
      const waitMs = (msg.retry_after_ms != null && isFinite(msg.retry_after_ms))
        ? Math.max(200, Math.min(10000, msg.retry_after_ms))
        : PREEMPT_RETRY_FALLBACK_MS;
      if (cs.preemptAttempts > PREEMPT_RETRY_MAX) {
        cs.status = 'idle';
        cs.note = 'Thiết bị vẫn chưa nhường được phiên tải đang chạy — thử lại sau ít phút.';
        rerender();
        return;
      }
      cs.status = 'requesting'; // keep the controls in the starting state
      cs.note = `Đang dừng phiên tải đang chạy để nhường cho đoạn này… (lần ${cs.preemptAttempts})`;
      rerender();
      if (cs.preemptRetryTimer) clearTimeout(cs.preemptRetryTimer);
      cs.preemptRetryTimer = setTimeout(() => {
        cs.preemptRetryTimer = null;
        // Retry the IDENTICAL request. NEVER arm this retry from a
        // playback_done:cancelled instead of `preempting` -- two tabs that
        // both retried on cancellation would cancel each other forever
        // (handoff §4.4 point 4, the loop guard).
        requestPlaybackToDevice(cs.clickAt);
      }, waitMs);
      return;
    }
    if (msg.message === 'busy') {
      // §7.5: neutral, never red. The owning session may belong to a
      // different tab/browser/page-load entirely.
      cs.status = 'idle';
      releaseUploadingFlag();
      cs.note = deviceCtx.uploadingCard
        ? mapErrorMessage('busy')
        : 'Thiết bị đang bận với một phiên tải KHÁC (tab/trình duyệt khác, hoặc lần bấm trước khi tải lại ' +
          'trang) — một đoạn 5 phút mất khoảng 1–2 phút. Đợi rồi bấm lại.';
      rerender();
      return;
    }
    cs.status = 'error';
    releaseUploadingFlag();
    cs.error = mapErrorMessage(msg.message);
    rerender();
  }, [cs, releaseUploadingFlag, deviceCtx, key, cameraNum, slotId, teardownPlayers, rerender, beginPlaylistPlayback, requestPlaybackToDevice, clearAckTimeout, armDataStallTimeout]);

  const handleDone = useCallback((msg) => {
    releaseUploadingFlag();
    if (msg.status === 'ok') {
      cs.transferComplete = true;
      if (cs.status !== 'error') cs.status = 'done'; // a decode failure survives a clean transfer (F1)
      rerender();
      return;
    }
    if (msg.status === 'cancelled') {
      // §4.4 point 5 / trap #12: whatever already landed stays playable —
      // never tear the player down, never treat this as an error. Covers
      // both `reason:"preempted"` (someone else's newer request won) and a
      // bare `cancelled` from older firmware. No note (2026-09-12 owner
      // feedback: the explanatory text was too long and unwanted) — the
      // card's own "đoạn tải dở" indicator already communicates this.
      cs.status = 'idle';
      cs.error = null;
      cs.note = null;
      rerender();
      return;
    }
    cs.status = 'error';
    cs.error = msg.message ? mapErrorMessage(msg.message) : 'Đoạn ghi không còn khả dụng';
    rerender();
  }, [cs, releaseUploadingFlag, rerender]);

  useEffect(() => {
    const unsubscribe = subscribePlayback((msg) => {
      if (msg.type === '__transport_closed__') {
        // E-S2-44: a dead socket must not leave this card (or, via the
        // shared uploading flag, every OTHER card) stuck forever.
        if (cs.deviceUploading || cs.status === 'requesting') {
          releaseUploadingFlag();
          if (cs.status !== 'done' && cs.status !== 'idle') {
            cs.status = 'error';
            cs.error = 'Mất kết nối tới máy chủ tín hiệu — vui lòng thử lại';
          }
          rerender();
        }
        return;
      }
      if (!isMine(msg)) return;
      if (msg.type === 'playback_ack') handleAck(msg);
      else if (msg.type === 'playback_done') handleDone(msg);
    });
    return () => {
      unsubscribe();
      teardownPlayers();
      clearUploadWatchdog();
      if (cs.preemptRetryTimer) { clearTimeout(cs.preemptRetryTimer); cs.preemptRetryTimer = null; }
      // A re-search (new time range) unmounts every current card while a
      // transfer may still be in flight on the device — without this the
      // shared uploadingCard registry would point at a dead card forever
      // (no functional block since the click is always allowed regardless,
      // but every other card's preemption-cost tooltip would go stale).
      deviceCtx.releaseUploading(key);
    };
    // Mount/unmount ONLY -- measured 2026-09-12: listing [isMine, handleAck,
    // handleDone] here made this effect tear down and rebuild on almost
    // every render of this card (any rerender()/forceUpdate() cascades
    // through some unstable identity upstream), and since the cleanup
    // unconditionally calls teardownPlayers() with nothing to ever restart
    // it, a single card update within the churn window would permanently
    // kill its hlsHandle/durationWatcher/jsPump moments after creation --
    // this was THE reproducible cause behind every "buffers partway then
    // frozen forever" report investigated this session, independent of
    // decode tier. Safe to pin to mount-time: isMine/handleAck/handleDone
    // only ever read `cs` (a ref) and deviceCtx/key/cameraNum/slotId (a
    // memoized context and stable props for this component's lifetime), so
    // a stale closure of them behaves identically to a fresh one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------------
  // Derived, render-facing values (mock FE `transferBtnMode` minus 'stop',
  // per the 2026-09-12 "bỏ Stop" decision; download-enable per the same
  // day's custom rule).
  const transferBtnMode = (cs.status === 'requesting' || cs.deviceUploading)
    ? 'sending'
    : (cs.transferComplete && cs.status !== 'error') ? 'done' : 'start';

  const durationSec = (cs.durationMs || 0) / 1000;
  const fullyBuffered = durationSec > 0 && cs.bufferedSec >= durationSec - FULLY_BUFFERED_TOLERANCE_SEC;
  const downloadEnabled = cs.everPlayable && (cs.bufferedSec >= DOWNLOAD_MIN_SEC || fullyBuffered);

  const otherUploading = deviceCtx.uploadingCard && deviceCtx.uploadingCard.cardKey !== key
    ? deviceCtx.uploadingCard
    : null;

  /**
   * §7.7: partial (snapshot the playlist's URI list now, concatenate raw
   * bytes) before the row is `ready`; the full presigned object once
   * `transferComplete`. Keyed on transferComplete, not status — a card that
   * finished its transfer but failed to DECODE is still fully downloadable
   * (mock FE F1).
   */
  const handleDownloadClick = useCallback(() => {
    if (!downloadEnabled) return;
    const task = (cs.transferComplete && cs.segmentUid)
      ? downloadFullSegment({ deviceSerial, camIndex0, camNum: cameraNum, tsUs, segmentUid: cs.segmentUid })
      : downloadPartialSegment({ playlistUrl: cs.videoUrl, deviceSerial, camNum: cameraNum, tsUs });
    task.catch((e) => {
      cs.note = `Tải xuống lỗi: ${e && e.message ? e.message : e}`;
      rerender();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs, downloadEnabled, deviceSerial, camIndex0, cameraNum, tsUs, rerender]);

  const downloadTooltip = !downloadEnabled
    ? 'Tải xuống (cần ≥60s đã tải, hoặc trọn đoạn nếu clip ngắn hơn 60s)'
    : (cs.transferComplete ? 'Tải xuống (đầy đủ, đã ghép trên storage)' : `Tải phần đã có (${fmtClipMmss(cs.bufferedSec || 0)})`);

  const handleScreenshot = useCallback(() => {
    // MSE: the <video> itself. WebCodecs: our own <canvas>, already 2D. WASM
    // (h265web.js/cylopengl): it renders via WebGL into a <canvas> it
    // creates itself inside the wasm container div — there is no other
    // capture point for that tier.
    let el;
    if (cs.decodePath === 'mse') el = videoRef.current;
    else if (cs.decodePath === 'webcodecs') el = canvasRef.current;
    else el = wasmContainerRef.current && wasmContainerRef.current.querySelector('canvas');
    if (!el) return null;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = el.videoWidth || el.width || 640;
      canvas.height = el.videoHeight || el.height || 360;
      canvas.getContext('2d').drawImage(el, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } catch (_) {
      return null;
    }
  }, [cs]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handlePlay = () => { cs.isPlaying = true; rerender(); };
    const handlePause = () => { cs.isPlaying = false; rerender(); };
    v.addEventListener('play', handlePlay);
    v.addEventListener('playing', handlePlay);
    v.addEventListener('pause', handlePause);
    v.addEventListener('ended', handlePause);
    return () => {
      v.removeEventListener('play', handlePlay);
      v.removeEventListener('playing', handlePlay);
      v.removeEventListener('pause', handlePause);
      v.removeEventListener('ended', handlePause);
    };
  }, [cs.videoUrl, rerender]);

  const handleSeek = useCallback((targetSec) => {
    if (targetSec == null || !isFinite(targetSec)) return;
    const durationSec = (cs.durationMs || 0) / 1000;
    const boundedSec = Math.max(0, Math.min(durationSec, targetSec));
    cs.currentTimeSec = boundedSec;
    cs.playClockAccumSec = boundedSec;
    if (cs.playClockResumedAtMs != null) {
      cs.playClockResumedAtMs = performance.now();
    }
    if (cs.decodePath === 'mse' && videoRef.current) {
      try {
        videoRef.current.currentTime = boundedSec;
      } catch (_) { /* ignore */ }
    } else if (cs.jsBackend && typeof cs.jsBackend.seek === 'function') {
      try {
        cs.jsBackend.seek(boundedSec);
      } catch (_) { /* ignore */ }
    }
    rerender();
  }, [cs, rerender]);

  return {
    key,
    status: cs.status,
    error: cs.error,
    note: cs.note,
    everPlayable: cs.everPlayable,
    isPlaying: Boolean(cs.isPlaying),
    overlayDismissed: cs.overlayDismissed,
    transferComplete: cs.transferComplete,
    fromCache: cs.fromCache,
    bufferedSec: cs.bufferedSec,
    currentTimeSec: cs.currentTimeSec,
    durationMs: cs.durationMs,
    videoUrl: cs.videoUrl,
    decodePath: cs.decodePath,
    segmentUid: cs.segmentUid,
    transferBtnMode,
    downloadEnabled,
    downloadTooltip,
    otherUploading,
    // refs to mount on the always-present <video>/<canvas>/<div> (hidden by
    // decodePath, per handoff §8 -- exactly one is visible per card at a time)
    videoRef,
    canvasRef,
    wasmContainerRef,
    // actions
    handleVideoAreaClick,
    handleTransferBtnClick: startTransfer,
    handleCancelSending,
    handlePlayToggle,
    handleSeek,
    handleScreenshot,
    handleDownloadClick,
  };
}
